import { Button, Input, message, Modal, Spin } from 'antd'
import React, { useEffect, useState } from 'react'
import { MdAdd } from 'react-icons/md'
import { HiExclamationCircle } from 'react-icons/hi'
import { FaCrown } from 'react-icons/fa'
import MembersList from './MembersList'
import validator from "validator";
import { deleteExpiredUser, inviteTeamMember } from '../../../../Utils/SupabaseServices/UserDatabase';
import { useLoadUser } from '../../../../Utils/customHooks/useLoadUser';
import { useAuth } from '../../../../hooks/useAuth';
import './AddMember.css';
import { supabase } from '../../../../config/supabaseClient';
import SlidingSidebar from '../../../../components/SlidingSidebar/SlidingSidebar';
import Sidebar from '../../Dashboard/Sidebar';
import Header from '../../../../components/Header/header';
import CopyToClipboard from '../../../../components/CopyToClipboard/CopyToClipboard';
import ImageSidebar from '../../../../components/Header/ImageSidebar';
import { SidebarStateProvider } from '../../../../context/SidebarStateContext';
import { loadStripe } from "@stripe/stripe-js";
import {
    Elements,
    PaymentElement,
    useStripe,
    useElements,
} from "@stripe/react-stripe-js";
import Stripe from 'stripe';

interface UserData {
    user_id: string;
    email: string;
    name: string;
    picture: string;
    pictureUrl: string;
    // Add other fields as needed
}

// const stripePromise = loadStripe(
//     "pk_test_51MavLUHoyGmWWOpVfkIMtmMb5oGGXCMNyg5j9CJgx1FAxnv54UUhLsazs4lyVK7c79u07lVbKDdtxoOXslGlm2r900TiOnwzCj"
// );

interface UpgradePaymentFormProps {
    onSuccess: () => void;
    onCancel: () => void;
}

function UpgradePaymentForm({ onSuccess, onCancel }: UpgradePaymentFormProps) {
    const stripe = useStripe();
    const elements = useElements();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!stripe || !elements) return;

        setLoading(true);
        setError("");

        try {
            const { error: paymentError, paymentIntent } = await stripe.confirmPayment({
                elements,
                confirmParams: {
                    return_url: window.location.origin + "/dashboard",
                },
                redirect: 'if_required'
            });

            if (paymentError) {
                setError(paymentError.message || "Payment failed");
            } else if (paymentIntent?.status === "succeeded") {
                message.success("Payment successful!");
                onSuccess();
            }
        } catch (err) {
            setError("An unexpected error occurred");
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} style={{ marginTop: '20px' }}>
            <PaymentElement />
            {error && (
                <div className="error-message" style={{ marginTop: '10px', color: 'red' }}>
                    <HiExclamationCircle />
                    {error}
                </div>
            )}
            <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
                <Button onClick={onCancel}>
                    Cancel
                </Button>
                <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    disabled={!stripe}
                >
                    Pay $10
                </Button>
            </div>
        </form>
    );
}

function AddMember(props: any) {
    const { userData, isEmailChanged } = props;
    const { isMember, session } = useAuth();
    const [username, setUsername] = useState(userData?.username);
    const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [error, setError] = useState("");
    const [refreshKey, setRefreshKey] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [showPaymentForm, setShowPaymentForm] = useState(false);
    const [clientSecret, setClientSecret] = useState("");
    const [paymentJustCompleted, setPaymentJustCompleted] = useState(false);

    // const {
    //     isLoading: userLoading,
    //     data: userData,
    // } = useLoadUser(username);

    // Add message listener for payment completion
    useEffect(() => {
        const handlePaymentMessage = async (event: MessageEvent) => {
            if (event.data?.type === 'PAYMENT_COMPLETED') {
                // Re-enable the screen
                document.body.style.pointerEvents = 'auto';
                // Clear the loading message
                message.destroy('paymentProcessing');

                if (event.data.status === 'success') {
                    message.success('Payment successful! You can now add team members.');
                    setShowUpgradeModal(false);
                    setPaymentJustCompleted(true); // Set the flag
                    // Retry sending the invitation if there was a pending one
                    if (email) {
                        handleAddTeam();
                    }
                } else {
                    message.error('Payment was not completed. Please try again.');
                }
            }
        };

        window.addEventListener('message', handlePaymentMessage);
        return () => window.removeEventListener('message', handlePaymentMessage);
    }, [email]);

    const checkExistingMember = async (teamMembers: any[], emailToCheck: string) => {
        // check if the email is in the team members
        const member = teamMembers.find(member => member.email === emailToCheck);
        // check if the email is in the user table
        const { data: userData, error: userError } = await supabase
            .from("user")
            .select("*")
            .eq("email", emailToCheck)
            .single();

        return member || userData;
    };

    const checkMemberLimit = async (teamMembers: any[]) => {
        // Count active and pending members
        const currentMembers = teamMembers?.filter(member =>
            member.status === 'active' ||
            (member.status === 'pending' && new Date(member.expires_at) > new Date())
        );

        return currentMembers?.length >= 1;
    };

    const handleAddTeam = async (from?: string) => {
        if (!validator.isEmail(email)) {
            setError("Please enter a valid email address");
            return;
        }

        if (!userData || typeof userData !== 'object') {
            setError("User data not available");
            return;
        }

        const userDataObj = userData as UserData;

        if (email.toLowerCase() === userDataObj.email?.toLowerCase()) {
            setError("You cannot invite yourself");
            return;
        }

        setIsSubmitting(true);
        try {
            const { data: creatorData } = await supabase
                .from("user")
                .select("team_members")
                .eq("user_id", userDataObj.user_id)
                .single();

            const existingMember = await checkExistingMember(creatorData?.team_members || [], email);
            if (existingMember) {
                setError(existingMember?.status === 'active'
                    ? "This user is already a team member" : existingMember?.status === 'pending'
                        ? "This email already has a pending invitation" : "This email is already in use"
                );
                setIsSubmitting(false);
                return;
            }
            else {
                // Only check member limit if payment wasn't just completed
                if (!paymentJustCompleted && from == "first") {
                    const reachedLimit = await checkMemberLimit(creatorData?.team_members || []);
                    if (reachedLimit) {
                        setIsSubmitting(false);
                        setIsAddMemberModalOpen(false);
                        setShowUpgradeModal(true);
                        return;
                    }
                }

                const result = await inviteTeamMember(userDataObj.user_id, email, "member");
                if (result.error) {
                    setError(result?.message || "Failed to send invitation");
                } else {
                    setEmail("");
                    setError("");
                    setIsAddMemberModalOpen(false);
                    message.success('Invitation sent successfully!');
                    // Force refresh the members list by updating refreshKey
                    setRefreshKey(prev => prev + 1);
                    // Reset the payment completed flag after successful invitation
                    setPaymentJustCompleted(false);
                    // Trigger a refetch of user data
                    await supabase
                        .from('user')
                        .select('team_members')
                        .eq('user_id', userDataObj.user_id)
                        .single();
                }
            }
        } catch (err: any) {
            setError(err.message || "Failed to send invitation");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Payment URL generation
    const userId = userData?.user_id; // however you get user_id
    const domain = window.location.origin; // your live domain

    const successUrl = `${domain}/add-team-payment-success?user_id=${userId}&session_id={CHECKOUT_SESSION_ID}&email=${email}`;
    const cancelUrl = `${domain}/add-team-payment-success?user_id=${userId}`;

    // Approch 1: Use the payment link from the stripe dashboard
    // const encodedSuccessUrl = encodeURIComponent(successUrl);
    // const encodedCancelUrl = encodeURIComponent(cancelUrl);

    // const finalPaymentLink = `https://payments.searchbubble.com/b/test_14AbJ1arr65igZTdxnaAw00?prefilled_success_url=${encodedSuccessUrl}&prefilled_cancel_url=${encodedCancelUrl}  `;

    // Approch 2: create a payment session using the stripe api and than open it in a new tab
    // const stripe_key = "sk_test_51MavLUHoyGmWWOpV7qCfWE1isX1NJesvdB22be4zzQ0WIrVqlZVblGb76RaHM9kmrdGz8ac3hhYZB9gB82EFcMTK004TMF1nId";
    const stripe_key = "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";
    const stripe = new Stripe(stripe_key, {
        apiVersion: '2022-11-15',
    });

    const createPaymentSession = async () => {
        try {
            const session = await stripe.checkout.sessions.create({
                mode: 'payment',
                line_items: [
                    {
                        price_data: {
                            currency: 'usd',
                            product: 'prod_SbzChYoRnnq01n', // Your actual product ID
                            unit_amount: 999, // Optional, if you want custom price
                        },
                        quantity: 1,
                    },
                ],
                metadata: {
                    user_id: userId!,
                    email: email,
                    payment_type: 'team_upgrade'
                },
                success_url: `${domain}/add-team-payment-success?session_id={CHECKOUT_SESSION_ID}&email=${email}&user_id=${userId}`,
                cancel_url: `${domain}/cancel`,
            });
            return session.url;
        } catch (error) {
            console.error('Error creating payment session:', error);
            message.error('Failed to initialize payment. Please try again.');
            return null;
        }
    };

    const handleUpgradeClick = async () => {
        // Show loading message
        message.loading({
            content: 'Initializing payment...',
            key: 'paymentProcessing'
        });

        try {
            const paymentUrl = await createPaymentSession();

            if (paymentUrl) {
                // Update loading message
                message.loading({
                    content: 'Please complete the payment in the new tab. Do not close this window.',
                    duration: 0,
                    key: 'paymentProcessing'
                });

                // Disable the current screen
                document.body.style.pointerEvents = 'none';

                // Open payment in new tab
                window.open(paymentUrl, '_blank');
            }
        } catch (error) {
            console.error('Payment initialization error:', error);
            message.error('Failed to initialize payment. Please try again.');
            document.body.style.pointerEvents = 'auto';
        }
    };

    return (
        <>
            {/* {userLoading ? <Spin /> : ( */}
            <div className="add-member-container">
                <div className="add-member-button-container">
                    <div className="account-tile-title">
                        <p>Team Members</p>
                    </div>
                    {/* Only show Add Team Member button if user is creator (not a member) */}
                    {(isMember && session?.user?.email === userData?.email) && (
                        <Button
                            className="add-member-button"
                            onClick={() => setIsAddMemberModalOpen(true)}
                        >
                            <MdAdd />
                            Add Team Member
                        </Button>
                    )}
                </div>

                {/* Always show MembersList */}
                <MembersList isEmailChanged={isEmailChanged} userData={userData} />

                {/* Only show add member modal and upgrade modal for creators */}
                {(isMember && session?.user?.email === userData?.email) && (
                    <>
                        <Modal
                            title="Add Team Member"
                            open={isAddMemberModalOpen}
                            focusTriggerAfterClose={false}
                            getContainer={false}
                            onCancel={() => {
                                setIsAddMemberModalOpen(false);
                                setError("");
                                setEmail("");
                            }}
                            footer={[
                                <Button
                                    key="cancel"
                                    onClick={() => {
                                        setIsAddMemberModalOpen(false);
                                        setError("");
                                        setEmail("");
                                    }}
                                >
                                    Cancel
                                </Button>,
                                <Button
                                    key="submit"
                                    type="primary"
                                    onClick={() => handleAddTeam("first")}
                                    loading={isSubmitting}
                                >
                                    Send Invitation
                                </Button>
                            ]}
                            className="add-member-modal"
                        >
                            <div className="modal-content">
                                <Input
                                    type="email"
                                    placeholder="Enter email address"
                                    value={email}
                                    onChange={(e) => {
                                        setError("");
                                        setEmail(e.target.value);
                                    }}
                                    status={error ? "error" : ""}
                                />
                                {error && (
                                    <div className="error-message">
                                        <HiExclamationCircle />
                                        {error}
                                    </div>
                                )}
                            </div>
                        </Modal>

                        <Modal
                            title={
                                <div className="upgrade-modal-title">
                                    <FaCrown className="crown-icon" />
                                    Upgrade to Add More Members
                                </div>
                            }
                            getContainer={false}
                            focusTriggerAfterClose={false}
                            open={showUpgradeModal}
                            onCancel={() => setShowUpgradeModal(false)}
                            footer={[
                                <Button
                                    key="cancel"
                                    onClick={() => setShowUpgradeModal(false)}
                                >
                                    Maybe Later
                                </Button>,
                                <Button
                                    key="upgrade"
                                    type="primary"
                                    className="upgrade-button"
                                    onClick={handleUpgradeClick}
                                >
                                    Upgrade Now
                                </Button>
                            ]}
                            className="upgrade-modal"
                        >
                            <div className="upgrade-content">
                                <p className="upgrade-message">
                                    You've reached the limit of 1 team member on the free plan.
                                    Upgrade to our premium plan to add unlimited team members and unlock more features!
                                </p>
                                <div className="premium-features">
                                    <div className="feature-item">
                                        ✨ Unlimited team members
                                    </div>
                                    <div className="feature-item">
                                        🚀 Advanced collaboration tools
                                    </div>
                                    <div className="feature-item">
                                        💎 Premium support
                                    </div>
                                </div>
                                <div className="upgrade-price">
                                    Only $10/month
                                </div>
                            </div>
                        </Modal>
                    </>
                )}
            </div>
            {/* )} */}
        </>
    )
}

export default AddMember